import { Center, Stack, Text, Title } from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { z } from 'zod';

import { useAcceptInvite } from '@/api/generated/registration/registration';
import { IconFigmaLock } from '@/assets/icons';
import { FormErrorMessage, MutationButton, PasswordField } from '@/components/common';
import { AuthCard } from '@/features/auth/components/AuthCard';
import { brandTextStyle } from '@/features/auth/components/authFieldStyles';
import { InvalidLinkCard } from '@/features/auth/components/InvalidLinkCard';
import { InvitationAcceptedCard } from '@/features/auth/components/InvitationAcceptedCard';
import { isNetworkError, isTimeoutError } from '@/utils/apiError';

interface AcceptInviteResult {
  orgName: string;
  role: string;
}

const schema = z
  .object({
    password: z.string().min(8),
    confirmPassword: z.string().min(8)
  })
  .refine((values) => values.password === values.confirmPassword, {
    message: 'passwordsMustMatch',
    path: ['confirmPassword']
  });

type FormValues = z.infer<typeof schema>;

export const AcceptInviteForm = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [acceptedResult, setAcceptedResult] = useState<AcceptInviteResult | null>(null);

  const acceptInviteMutation = useAcceptInvite({
    mutation: {
      onSuccess: (data) => setAcceptedResult(data as unknown as AcceptInviteResult)
    }
  });

  const form = useForm<FormValues>({
    validate: zodResolver(schema),
    initialValues: { password: '', confirmPassword: '' }
  });

  const handleSubmit = (values: FormValues) => {
    if (!token) return;
    acceptInviteMutation.mutate({ data: { token, password: values.password } });
  };

  // Isti obrazac kao ResetPasswordForm — timeout/network (ili isPaused) ne
  // znači da je token nevažeći, korisnik treba priliku pokušati ponovno na
  // ISTOM linku.
  const isConnectivityError =
    acceptInviteMutation.isPaused ||
    (acceptInviteMutation.isError &&
      (isTimeoutError(acceptInviteMutation.error) || isNetworkError(acceptInviteMutation.error)));

  if (acceptedResult) {
    return <InvitationAcceptedCard orgName={acceptedResult.orgName} role={acceptedResult.role} />;
  }

  if (!token || (acceptInviteMutation.isError && !isConnectivityError)) {
    return (
      <InvalidLinkCard
        title={t('resetPassword.invalidTitle')}
        message={t('resetPassword.invalidMessage')}
        hint={t('resetPassword.invalidHint')}
        retryLabel={t('resetPassword.invalidRetry')}
        backTo="/login"
      />
    );
  }

  return (
    <AuthCard px={32} py={36} w={{ base: '100%', md: 440 }}>
      <Stack align="center" gap={14}>
        <Center
          w={56}
          h={56}
          style={{
            borderRadius: 12,
            backgroundColor: 'var(--color-accent-secondary-soft)',
            color: 'var(--color-icon-accent)'
          }}
        >
          <IconFigmaLock size={24} />
        </Center>
        <Title order={2} fz={24} fw={700} ta="center" style={brandTextStyle}>
          {t('acceptInvite.title')}
        </Title>
        <Text size="sm" ta="center" style={{ color: 'var(--color-text-secondary)' }}>
          {t('acceptInvite.subtitle')}
        </Text>

        {isConnectivityError && (
          <FormErrorMessage
            message={
              !acceptInviteMutation.isPaused && isTimeoutError(acceptInviteMutation.error)
                ? t('errors.timeout.message')
                : t('errors.network.message')
            }
          />
        )}

        <form onSubmit={form.onSubmit(handleSubmit)} style={{ width: '100%' }} noValidate>
          <Stack gap="md">
            <PasswordField
              label={t('acceptInvite.password')}
              required
              {...form.getInputProps('password')}
            />
            <PasswordField
              label={t('acceptInvite.confirmPassword')}
              required
              error={form.errors.confirmPassword ? t('acceptInvite.passwordsMustMatch') : undefined}
              {...form.getInputProps('confirmPassword')}
            />

            <MutationButton
              type="submit"
              fullWidth
              isPending={acceptInviteMutation.isPending}
              isPaused={acceptInviteMutation.isPaused}
            >
              {t('acceptInvite.submit')}
            </MutationButton>
          </Stack>
        </form>
      </Stack>
    </AuthCard>
  );
};
